import React, { FC } from 'react';

import { Formik } from 'formik';
import {
  ActionBar,
  Actions,
  Input,
} from 'components/Modals/CategoryModal/styles';
import { Title } from 'pages/Main/Settings/General/styles';
import { Subtitle } from 'pages/Main/Support/PhoneNumbers/styles';
import Address from 'components/Address';
import MaskedInput from 'components/Address/MaskedInput';
import { AddressType } from 'data/requests/submitBIF';
import type { BIFData } from 'data/types/BIF';
import { validationSchema } from './constants';
import {
  Container,
  Form,
  RowContainer,
  StyledField,
  Box,
  FieldDescription,
} from './styles';
import { Header, Tabs, Tab, Button } from '../styles';
import useConnect from './connect';

const BankingLegal: FC = () => {
  const [legalAddress, setLegalAddress] = React.useState<AddressType | null>(
    null,
  );
  const [ownerPhone, setOwnerPhone] = React.useState<string | null>(null);
  const [routingNumber, setRoutingNumber] = React.useState<string | null>(null);

  const { BIF, isLoading, handleSubmit } = useConnect();
  const routingNumberExp: (string | RegExp)[] = [
    /\d|\*/,
    /\d|\*/,
    /\d|\*/,
    '-',
    /\d|\*/,
    /\d|\*/,
    /\d/,
    '-',
    /\d/,
    /\d/,
    /\d/,
  ];

  React.useEffect(() => {
    if (BIF) {
      /* Workaround to set the values since React-IMask
       * doesn't support setting the value prop on the component directly,
       * so they're updated here and passed down as props to the component.
       */
      setOwnerPhone(BIF.owner_phone);
      setRoutingNumber(BIF.routing_number);
    }
  }, [BIF]);

  return (
    <>
      <Header style={{ flexWrap: 'wrap' }}>
        <Tabs>
          <Tab label="General" to="/settings/general" />
          <Tab label="Working Hours" to="/settings/working-hours" />
          <Tab label="Banking & Legal" to="/settings/banking-legal" />
        </Tabs>
      </Header>
      <Formik
        onSubmit={async (e: BIFData) => {
          await handleSubmit({
            payload: e,
            legalAddress: legalAddress as AddressType,
          });
        }}
        initialValues={BIF as never}
        enableReinitialize
        validationSchema={validationSchema}
      >
        {() => (
          <Form noValidate>
            <div style={{ display: 'none' }}>
              <Title>Restaurant Info</Title>
              <Subtitle>
                These details are only used for banking verification and will
                not update your general information.
              </Subtitle>
              <Container style={{ paddingBottom: '3rem' }}>
                <RowContainer>
                  <Box>
                    <StyledField
                      name="restaurant_name"
                      as={Input}
                      label="Restaurant name"
                      placeholder="Enter restaurant name"
                      disabled={isLoading}
                    />
                  </Box>
                  <Box>
                    <Address
                      Component={StyledField}
                      ComponentProps={{
                        name: 'restaurant_address',
                        label: 'Restaurant Address',
                        placeholder: 'Enter Address',
                        disabled: false,
                      }}
                    />
                  </Box>
                </RowContainer>
                <RowContainer>
                  <Box>
                    <StyledField
                      name="owner_name"
                      as={Input}
                      label="Owner Name"
                      placeholder="Enter Name"
                      disabled={isLoading}
                    />
                  </Box>
                  <Box>
                    <MaskedInput
                      mask="(999) 999-9999"
                      Component={StyledField}
                      defaultValue={ownerPhone || ''}
                      ComponentProps={{
                        name: 'owner_phone',
                        label: 'Owner Cell Phone',
                        placeholder: '(123) 456-7890',
                        as: Input,
                      }}
                      disabled={isLoading}
                    />
                  </Box>
                </RowContainer>
                <RowContainer>
                  <Box>
                    <StyledField
                      name="restaurant_email"
                      as={Input}
                      label="Restaurant Email"
                      placeholder="Enter Restaurant Email"
                      disabled={isLoading}
                    />
                  </Box>
                </RowContainer>
              </Container>
            </div>
            <Title style={{ paddingTop: '2rem' }}>Banking Details</Title>
            <Container>
              <RowContainer>
                <Box>
                  <StyledField
                    name="account_holder_name"
                    as={Input}
                    label="Bank Account Holder's Name"
                    placeholder="Enter Name"
                    disabled={isLoading}
                  />
                  <FieldDescription>
                    Whose name is the bank account registered under?
                  </FieldDescription>
                </Box>
                <Box>
                  <Address
                    Component={StyledField}
                    ComponentProps={{
                      name: 'legal_address',
                      label: 'Legal Entity Address',
                      placeholder: 'Enter Address',
                      disabled: false,
                    }}
                    setAddress={setLegalAddress}
                  />
                  <FieldDescription>
                    What address is your legal entity listed under?
                  </FieldDescription>
                </Box>
              </RowContainer>
              <RowContainer>
                <Box>
                  <MaskedInput
                    mask={routingNumberExp}
                    defaultValue={routingNumber || ''}
                    Component={StyledField}
                    ComponentProps={{
                      name: 'routing_number',
                      label: 'Routing Number',
                      placeholder: '000-000-000',
                      as: Input,
                    }}
                    disabled={isLoading}
                  />
                  <FieldDescription>
                    What is the routing number for the bank account to which
                    Superorder deposits?
                  </FieldDescription>
                </Box>
                <Box>
                  <StyledField
                    name="account_number"
                    as={Input}
                    label="Account Number"
                    placeholder="Enter Account Number"
                    disabled={isLoading}
                  />
                  <FieldDescription>
                    What is the account number for the bank to which Superorder
                    deposits?
                  </FieldDescription>
                </Box>
              </RowContainer>
            </Container>
            <ActionBar>
              <Actions>
                <Button
                  type="button"
                  variant="outlined"
                  color="secondary"
                  style={{ marginLeft: 'auto' }}
                >
                  Cancel
                </Button>
                <Button color="secondary" type="submit" disabled={isLoading}>
                  Save
                </Button>
              </Actions>
            </ActionBar>
          </Form>
        )}
      </Formik>
    </>
  );
};

export default BankingLegal;
